import { Formik } from 'formik';
import htmlReactParser from 'html-react-parser';
import React, { memo, useEffect, useMemo, useState } from 'react';

import RadioButtonGroup from '@components/Form/RadioButtonGroup';
import useOffers from '@hooks/useOffers';
import useWindowSize from '@hooks/useWindowSize';
import { OfferOption } from '@utils/backend-api/subscribe';
import { WIDTH_SM } from '@utils/constants';
import { LeadType } from '@utils/types';

import SignUpForm from './SignUpForm';
import { FormValues } from './index';

const INITIAL_VALUES: FormValues = {
  selectedOffer: '',
};

const OfferPriceDetails = memo(({ offer }: { offer: OfferOption }) => {
  const currentPriceToDisplay = offer.currentPriceDisplayOverride || offer.currentPrice;
  const hasDiscount = offer.originalPrice !== offer.currentPrice;
  const originalPriceToDisplay = offer.originalPriceDisplayOverride || offer.originalPrice;

  return (
    <div className="mb-8 flex flex-wrap items-center">
      <span className="font-suisse-screen text-disclosure-lg font-medium text-black">
        ${currentPriceToDisplay}
      </span>{' '}
      {hasDiscount && (
        <span className="ml-8 font-suisse-screen text-disclosure-lg text-grayscale-gray line-through sm:ml-8">
          ${originalPriceToDisplay}
        </span>
      )}
      {offer.billingFrequencyOverride && (
        <span className="ml-8 font-suisse-screen text-disclosure-md text-grayscale-charcoal sm:ml-8">
          {offer.billingFrequencyOverride}
        </span>
      )}
    </div>
  );
});

OfferPriceDetails.displayName = 'OfferPriceDetails';

interface OfferSelectionFormSectionProps {
  proOfferCode: string;
  annualOfferCode: string;
  buttonText: string;
  leadType: LeadType;
}

const OfferSelectionFormSection = ({
  proOfferCode,
  annualOfferCode,
  buttonText,
  leadType,
}: OfferSelectionFormSectionProps) => {
  const [initialValues, setInitialValues] = useState<FormValues>(INITIAL_VALUES);
  const { data: offersData, isLoading: isLoadingOffer } = useOffers({
    offer_codes: [proOfferCode, annualOfferCode],
  });
  const { width: windowWidth } = useWindowSize();

  const isSmallScreen = useMemo(() => windowWidth < WIDTH_SM, [windowWidth]);

  const { proOfferData, annualOfferData } = useMemo(() => {
    if (!offersData || offersData.length === 0) {
      return { proOfferData: undefined, annualOfferData: undefined };
    }
    return {
      proOfferData: offersData.find((offer) => offer.isPro),
      annualOfferData: offersData.find((offer) => offer.isAnnual),
    };
  }, [offersData]);

  const proDescription = useMemo(() => {
    if (!proOfferData) return null;
    return isSmallScreen
      ? htmlReactParser(proOfferData.shortDescription || '')
      : htmlReactParser(proOfferData.description || '');
  }, [proOfferData, isSmallScreen]);

  const annualDescription = useMemo(() => {
    if (!annualOfferData) return null;
    return isSmallScreen
      ? htmlReactParser(annualOfferData.shortDescription || '')
      : htmlReactParser(annualOfferData.description || '');
  }, [annualOfferData, isSmallScreen]);

  useEffect(() => {
    if (proOfferData?.code) {
      setInitialValues({
        selectedOffer: proOfferData.code,
      });
    }
  }, [proOfferData?.code]);

  const offerOptions = useMemo(() => {
    if (!proOfferData || !annualOfferData) return [];

    return [
      {
        labelClassName: '!text-headline-xxs text-pro-gold !items-start flex-shrink-0',
        radioButtonContainerClassName: 'flex-shrink-0',
        radioButtonClassName: '!bg-pro-gold',
        label: proOfferData.title,
        value: proOfferData.code,
        children: (
          <>
            <OfferPriceDetails offer={proOfferData} />
            <p className="m-0 font-suisse-intl text-description-sm text-grayscale-charcoal">
              {proDescription}
            </p>
          </>
        ),
      },
      {
        labelClassName: '!text-headline-xxs !items-start text-primary-magenta flex-shrink-0',
        radioButtonContainerClassName: 'flex-shrink-0',
        label: annualOfferData.title,
        value: annualOfferData.code,
        children: (
          <>
            <OfferPriceDetails offer={annualOfferData} />
            <p className="m-0 font-suisse-intl text-description-sm text-grayscale-charcoal">
              {annualDescription}
            </p>
          </>
        ),
      },
    ];
  }, [proOfferData, annualOfferData, proDescription, annualDescription]);

  if (
    isLoadingOffer ||
    !offersData ||
    offersData.length === 0 ||
    !proOfferData ||
    !annualOfferData
  ) {
    return null;
  }

  return (
    <div className="w-full flex-1 bg-white px-32 py-32 md:px-64 md:py-48 lg:px-90">
      <Formik enableReinitialize initialValues={initialValues} onSubmit={() => {}}>
        <div>
          <RadioButtonGroup
            name="selectedOffer"
            options={offerOptions}
            optionClassName="flex flex-col !items-start !mb-32"
          />
          <SignUpForm buttonText={buttonText} leadType={leadType} />
        </div>
      </Formik>
    </div>
  );
};

export default OfferSelectionFormSection;
